import moment from 'moment';
export const dateUtils = {
    methods: {
        timeUntil(datetime, format = 'humanize') {
            // If no datetime is provided, return 'N/A'
            if (!datetime) {
                return; //N/A';
            }

            // Get the current time and target time
            const now = new Date();
            const target = new Date(datetime);

            // Calculate the duration in milliseconds
            const durationInMs = target - now;

            // If the target date is in the past, return 'N/A' or handle this as needed
            if (durationInMs <= 0) {
                return '00';
            }

            // Use moment's duration functionality
            const duration = moment.duration(durationInMs);

            // If format is 'humanize', return the humanized version
            if (format === 'humanize') {
                return duration.humanize(true); // 'true' to show relative time (like 'in 5 minutes')
            }

            // For custom formats like 'HH:MM' or 'HH:MM:SS'
            const hours = Math.floor(duration.asHours());
            const minutes = Math.floor(duration.minutes());
            const seconds = Math.floor(duration.seconds());

            // Depending on the requested format, return the appropriate string
            switch (format) {
                case 'HH':
                    return `${hours.toString().padStart(2, '0')}`;
                case 'HH:MM':
                    return `${hours
                        .toString()
                        .padStart(2, '0')}:${minutes
                        .toString()
                        .padStart(2, '0')}`;
                case 'HH:MM:SS':
                    return `${hours
                        .toString()
                        .padStart(2, '0')}:${minutes
                        .toString()
                        .padStart(2, '0')}:${seconds
                        .toString()
                        .padStart(2, '0')}`;
                default:
                    return 'Invalid format'; // Fallback if the format is unknown
            }
        },

        parseDuration(duration) {
            if (!duration) {
                return 0; // If the duration is missing, return 0
            }
            const [hours, minutes, seconds] = duration.split(':').map(Number);
            return (hours * 3600 + minutes * 60 + seconds) * 1000;
        },
        roundToNearestHour(datetime) {
            if (!datetime) {
                return 'N/A';
            }
            const date = new Date(datetime);
            const minutes = date.getMinutes();
            if (minutes >= 30) {
                date.setHours(date.getHours() + 1);
            }
            date.setMinutes(0, 0, 0);
            return this.formatDateTime(date, 'YYYY-MM-DD HH:00:00');
        },
        formatDateTime(datetime, timeFormat = 'YYYY-MM-DD HH:mm:ss') {
            if (!datetime) {
                return 'N/A';
            }
            const date = new Date(datetime);
            const formatMap = {
                YYYY: date.getFullYear(),
                MM: String(date.getMonth() + 1).padStart(2, '0'),
                DD: String(date.getDate()).padStart(2, '0'),
                HH: String(date.getHours()).padStart(2, '0'),
                mm: String(date.getMinutes()).padStart(2, '0'),
                ss: String(date.getSeconds()).padStart(2, '0')
            };

            return timeFormat.replace(
                /YYYY|MM|DD|HH|mm|ss/g,
                matched => formatMap[matched]
            );
        },
        getDifference(start, end) {
            if (start === null || end === null) {
                return 'N/A';
            }
            const diffMs = new Date(end) - new Date(start);
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffMinutes = Math.floor((diffMs / (1000 * 60)) % 60);
            const diffSeconds = Math.floor((diffMs / 1000) % 60);
            return `${String(diffHours).padStart(2, '0')}:${String(
                diffMinutes
            ).padStart(2, '0')}:${String(diffSeconds).padStart(2, '0')}`;
        },
        formatHumanReadableDateTime(datetime) {
            if (!datetime) {
                return 'N/A';
            }
            const date = new Date(datetime);
            const formattedTime = this.formatTimeOfDay(
                `${date.getHours()}:${date.getMinutes()}`
            );
            return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(
                date.getDate()
            ).padStart(2, '0')}-${date.getFullYear()} ${formattedTime}`;
        },
        formatIndependentTime(time, withSeconds = false) {
            if (!time) {
                return 'N/A';
            } else if (
                typeof time !== 'string' ||
                !time.match(/^\d{1,2}:\d{1,2}(:\d{1,2})?$/)
            ) {
                return 'N/A';
            }
            // Handles general time formatting like "55:23:00"
            const [hours, minutes, seconds] = time.split(':').map(Number);
            if (withSeconds) {
                return `${String(hours).padStart(2, '0')}h ${String(
                    minutes
                ).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
            }
            return `${String(hours).padStart(2, '0')}h ${String(
                minutes
            ).padStart(2, '0')}m`;
        },
        formatTimeOfDay(time, withSeconds = false) {
            if (!time) {
                return 'N/A';
            }
            // Handles time-of-day formatting like "14:30" with AM/PM classification
            const [hours, minutes, seconds] = time.split(':').map(Number);
            const formattedHours = hours % 12 || 12;
            const ampm = hours >= 12 ? 'PM' : 'AM';
            let timeString = `${formattedHours}:${String(minutes).padStart(
                2,
                '0'
            )} ${ampm}`;
            if (withSeconds) {
                timeString += ` ${String(seconds).padStart(2, '0')}s`;
            }
            return timeString;
        },
        addDays(datetime, days) {
            const date = new Date(datetime);
            date.setDate(date.getDate() + days);
            return date;
        },
        subtractDays(datetime, days) {
            const date = new Date(datetime);
            date.setDate(date.getDate() - days);
            return date;
        },
        addHours(datetime, hours) {
            const date = new Date(datetime);
            date.setHours(date.getHours() + hours);
            return date;
        },
        subtractHours(datetime, hours) {
            const date = new Date(datetime);
            date.setHours(date.getHours() - hours);
            return date;
        },
        startOfDay(datetime) {
            const date = new Date(datetime);
            date.setHours(0, 0, 0, 0);
            return date;
        },
        endOfDay(datetime) {
            const date = new Date(datetime);
            date.setHours(23, 59, 59, 999);
            return date;
        },
        isWeekend(datetime) {
            const date = new Date(datetime);
            const day = date.getDay();
            return day === 0 || day === 6;
        },
        getDayOfWeek(datetime) {
            const date = new Date(datetime);
            const days = [
                'Sunday',
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday'
            ];
            return days[date.getDay()];
        },
        isLeapYear(year) {
            return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        },
        daysInMonth(year, month) {
            return new Date(year, month, 0).getDate();
        },
        compareDates(date1, date2) {
            const d1 = new Date(date1).setHours(0, 0, 0, 0);
            const d2 = new Date(date2).setHours(0, 0, 0, 0);
            if (d1 > d2) return 1;
            if (d1 < d2) return -1;
            return 0;
        },
        parseDate(dateString) {
            return new Date(Date.parse(dateString));
        }
    }
};
